import React, { useCallback, useRef, useState } from 'react';
import styled from 'styled-components';
import { Separator, Col, Row, FlexView } from '@cuemath/comps';
import { Text, Button } from '@cuemath/web-comps';
import { usePostMessage } from 'actions/support-agent';
import COLORS from 'constants/colors';
import {
  EventRow,
  EventIcon,
  MsgContainer,
  MsgContainerAgent,
  MsgAvatar,
  MsgHead,
  MsgName,
  MsgRole,
  MsgTime,
  MsgBodyUser,
  MsgBodyBot,
  MsgBodyAgent,
  SystemMsg,
  NoteCard,
  NoteTag,
  AttachImage,
  FileCard,
  FileCardUser,
  FileIconWrap,
  FileIconWrapUser,
  FileName,
  ExternalMsgCard,
  ExternalMsgStrip,
  ExternalMediumLabel,
  ExternalSenderRow,
  ExternalStripRight,
  ExternalLockChip,
  DirectionLabel,
  ExternalHeaderGrid,
  ExternalHeaderValue,
  ExternalBody,
  ReplyToggleBtn,
  ReplyFormWrap,
  BccToggle,
  CcBccToggleRow,
  EmailFieldRow,
  EmailFieldLabel,
  EmailFieldInput,
  FilterDropdownWrap,
  FilterDropdownTrigger,
  FilterDropdownMenu,
  FilterDropdownItem,
  ModalErrorMsg,
  HiddenFileInput,
  AttachmentChip,
  AttachmentChipName,
  AttachmentChipRemove,
  AttachBtn,
  EmailComposerArea,
  SA_COLORS,
} from '../styled-comps';
import {
  SUPPORT_EMAIL_PREFIX,
  FROM_EMAIL_OPTIONS,
  ERROR_AUTO_CLEAR_MS,
} from '../constants';
import parse from 'html-react-parser';
import { getFileNameFromUrl, commitDraftToList } from '../utils';
import { sanitizeHtml, hasRenderableBody, shouldShowEmailSubject } from '../email-body';
import Icon from './icon';
import AttachIcon from 'img/paperclip.svg';
import MarkdownText from './markdown-text';
import RecipientChipsInput from './recipient-chips-input';
import useAttachmentUpload from './use-attachment-upload';

// ── Styled helpers ─────────────────────────────────────────────────────

const HtmlBody = styled.div`
  word-break: break-word;
`;

const EmailRecipientsInline = styled.div`
  border-bottom: 1px solid
    ${({ $dark }) => ($dark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)')};
  padding-bottom: 6px;
  margin-bottom: 6px;
`;

const EmailRecipientLine = styled(Text)`
  && {
    font-size: 11px;
    color: ${({ $dark }) =>
      $dark ? 'rgba(255, 255, 255, 0.7)' : SA_COLORS.TEXT_SECONDARY};
    font-family: monospace;
    line-height: 1.5;
  }
`;

const EmailLabel = styled.span`
  display: inline-block;
  min-width: 32px;
`;

const EventTitle = styled(Text)`
  && {
    font-size: 13px;
    font-weight: 600;
    color: ${SA_COLORS.TEXT_PRIMARY};
  }
`;

const EventSub = styled(Text)`
  && {
    font-size: 12px;
    color: ${SA_COLORS.TEXT_TERTIARY};
  }
`;

const EventInfo = styled(Col)`
  && {
    gap: 2px;
    justify-content: center;
  }
`;

const MsgColumn = styled(FlexView)`
  && {
    gap: 6px;
    min-width: 0;
    flex: 1;
  }
`;

const ExternalAvatar = styled(MsgAvatar)`
  width: 26px;
  height: 26px;
  font-size: 10px;
  font-weight: 700;
`;

const SentByAnnotation = styled(Text)`
  && {
    font-size: 11px;
    font-style: italic;
    color: ${SA_COLORS.TEXT_TERTIARY};
    padding: 0 2px;
  }
`;

const SentByName = styled.span`
  font-weight: 600;
  font-style: normal;
  color: ${SA_COLORS.TEXT_SECONDARY};
`;

const StructuredButtonsWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`;

const StructuredBtn = styled.button`
  display: inline-block;
  padding: 5px 12px;
  font-size: 12px;
  color: ${SA_COLORS.INFO_600};
  background: transparent;
  border: 1px solid ${SA_COLORS.INFO_600};
  border-radius: 14px;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: ${SA_COLORS.INFO_600}11;
  }
`;

const NoteHeader = styled(Row)`
  && {
    align-items: center;
    gap: 6px;
  }
`;

const NoteTitle = styled(Text)`
  && {
    font-size: 13px;
    font-weight: 600;
    color: ${SA_COLORS.GOLD_900};
  }
`;

const NoteBody = styled(FlexView)`
  && {
    font-size: 13px;
    line-height: 1.5;
    color: ${SA_COLORS.GOLD_900};
  }
`;

const SystemBody = styled(Text)`
  && {
    font-size: 13px;
    line-height: 1.4;
  }
`;

// ── Role helpers ───────────────────────────────────────────────────────

const getRoleLabel = item => {
  if (item.who === 'external') return 'External';
  if (!item.role) return '';
  if (item.who === 'bot') return 'AI bot';
  if (item.who === 'agent') return 'Agent';
  return 'Customer';
};

const getName = item => item.name || '';

// ── Event ──────────────────────────────────────────────────────────────

const EventItem = ({ item }) => (
  <EventRow>
    <EventIcon>
      <Icon name={item.icon || 'chat'} size={16} />
    </EventIcon>
    <EventInfo>
      <EventTitle>{item.title}</EventTitle>
      {item.sub && <EventSub>{item.sub}</EventSub>}
    </EventInfo>
  </EventRow>
);

// ── Attachment rendering ───────────────────────────────────────────────

const AttachmentContent = ({
  item,
  isUser,
  isDark = false,
  subjectFallback = false,
  mediaLast = false,
}) => {
  const hasMedia = item.mediaUrl && item.messageType !== 'TEXT';

  const bodyNode = item.htmlContent ? (
    <HtmlBody>
      {parse(sanitizeHtml(item.htmlContent), {
        replace: node => {
          if (node.name === 'img') {
            const { src, alt, width, height } = node.attribs || {};
            return (
              <AttachImage
                src={src}
                alt={alt || ''}
                width={width}
                height={height}
                onClick={() => window.open(src, '_blank')}
              />
            );
          }
        },
      })}
    </HtmlBody>
  ) : item.body ? (
    <MarkdownText>{item.body}</MarkdownText>
  ) : null;

  const labelColor = isDark ? SA_COLORS.NEUTRAL_100 : SA_COLORS.TEXT_SECONDARY;
  const emptyBody = !hasMedia && !hasRenderableBody(item);
  let contentNode = bodyNode;

  if (subjectFallback && emptyBody) {
    contentNode = shouldShowEmailSubject(item) ? (
      <>
        <Text variant="eyebrow2" size={11} weight={600} color={labelColor}>
          Subject
        </Text>
        <Text size={14}>{(item.emailSubject || '').trim()}</Text>
      </>
    ) : (
      <Text size={13} color={labelColor}>
        (no message content)
      </Text>
    );
  }

  if (!hasMedia) return contentNode;

  const FileCardComp = isUser ? FileCardUser : FileCard;
  const IconWrapComp = isUser ? FileIconWrapUser : FileIconWrap;

  const mediaNode =
    item.messageType === 'IMAGE' ? (
      <AttachImage
        src={item.mediaUrl}
        alt={item.body || 'Image'}
        onClick={() => window.open(item.mediaUrl, '_blank')}
      />
    ) : (
      <FileCardComp href={item.mediaUrl} target="_blank" rel="noopener noreferrer">
        <IconWrapComp>
          <Icon name="link" size={16} />
        </IconWrapComp>
        <FileName>{item.fileName || getFileNameFromUrl(item.mediaUrl)}</FileName>
      </FileCardComp>
    );

  if (mediaLast) {
    return (
      <>
        {bodyNode}
        {bodyNode && <Separator height={6} transparent />}
        {mediaNode}
      </>
    );
  }

  return (
    <>
      {mediaNode}
      {bodyNode && (
        <>
          <Separator height={6} transparent />
          {bodyNode}
        </>
      )}
    </>
  );
};

// ── Message ────────────────────────────────────────────────────────────

const MsgItem = ({ item, emailFrom, emailTo, onOptionSelect, userOnRight }) => {
  const isUser = item.who === 'user';
  const isBot = item.who === 'bot';
  const isAgent = item.who === 'agent';

  const rightSide = userOnRight ? isUser : !isUser;

  const Container = rightSide ? MsgContainerAgent : MsgContainer;

  const BodyComponent = isUser ? MsgBodyUser : isBot ? MsgBodyBot : MsgBodyAgent;

  const avatarVariant = isUser ? 'user' : isBot ? 'bot' : 'agent';
  const avatarContent = isBot ? (
    <Icon name="sparks" size={14} />
  ) : (
    item.initials ||
    getName(item)
      .slice(0, 2)
      .toUpperCase()
  );

  const roleLabel = getRoleLabel(item);

  return (
    <Container flexDirection={rightSide ? 'row-reverse' : 'row'}>
      <MsgAvatar $variant={avatarVariant}>{avatarContent}</MsgAvatar>
      <MsgColumn alignItems={rightSide ? 'end' : 'start'}>
        <MsgHead
          alignItems="center"
          justifyContent={rightSide ? 'flex-end' : 'flex-start'}
        >
          <MsgName>{getName(item)}</MsgName>
          {roleLabel && <MsgRole>{roleLabel}</MsgRole>}
          {item.ts && <MsgTime>{item.ts}</MsgTime>}
        </MsgHead>
        <Separator height={4} transparent />
        <BodyComponent $rightSide={rightSide}>
          {emailFrom && (isAgent || isUser) && (
            <EmailRecipientsInline $dark={isAgent}>
              <EmailRecipientLine $dark={isAgent}>
                <EmailLabel>From</EmailLabel>:{' '}
                {isAgent ? emailFrom : item.senderEmail || emailTo}
              </EmailRecipientLine>
              <EmailRecipientLine $dark={isAgent}>
                <EmailLabel>To</EmailLabel>:{' '}
                {isAgent
                  ? emailTo
                  : item.emailToList
                  ? item.emailToList
                      .map(e => (e.startsWith(SUPPORT_EMAIL_PREFIX) ? emailFrom || e : e))
                      .join(', ')
                  : emailFrom}
              </EmailRecipientLine>
              {item.emailCc && item.emailCc.length > 0 && (
                <EmailRecipientLine $dark={isAgent}>
                  <EmailLabel>CC</EmailLabel>:{' '}
                  {item.emailCc
                    .map(e => (e.startsWith(SUPPORT_EMAIL_PREFIX) ? emailFrom || e : e))
                    .join(', ')}
                </EmailRecipientLine>
              )}
              {item.emailBcc && item.emailBcc.length > 0 && (
                <EmailRecipientLine $dark={isAgent}>
                  <EmailLabel>BCC</EmailLabel>: {item.emailBcc.join(', ')}
                </EmailRecipientLine>
              )}
            </EmailRecipientsInline>
          )}
          <AttachmentContent
            item={item}
            isUser={isUser}
            isDark={isAgent}
            subjectFallback
            mediaLast
          />
          {item.structuredData?.options && onOptionSelect && (
            <StructuredButtonsWrap>
              {item.structuredData.options.map((opt, idx) => (
                <StructuredBtn
                  key={idx}
                  onClick={onOptionSelect ? () => onOptionSelect(opt) : undefined}
                >
                  {opt.label || opt.value || ''}
                </StructuredBtn>
              ))}
            </StructuredButtonsWrap>
          )}
        </BodyComponent>
        {isUser && item.sentBy && (
          <SentByAnnotation>
            Sent by <SentByName>{item.sentByName || item.sentBy}</SentByName> on behalf of
            user
          </SentByAnnotation>
        )}
      </MsgColumn>
    </Container>
  );
};

// ── System ─────────────────────────────────────────────────────────────

const SystemItem = ({ item }) => {
  const tint = item.tint || 'info';
  const iconName = tint === 'error' ? 'close' : tint === 'warning' ? 'hint' : 'hint';

  return (
    <SystemMsg $variant={tint}>
      <Icon name={iconName} size={16} />
      <SystemBody>
        {item.title && (
          <>
            <Text variant="body2">{item.title}</Text>
            <Separator height={4} transparent />
          </>
        )}
        {item.sub && (
          <>
            <Text variant="caption">{item.sub}</Text>
            <Separator height={4} transparent />
          </>
        )}
        {item.body && <MarkdownText>{item.body}</MarkdownText>}
      </SystemBody>
    </SystemMsg>
  );
};

// ── Note ───────────────────────────────────────────────────────────────

const NoteEmailMeta = styled.div`
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  padding-bottom: 6px;
  margin-bottom: 6px;
`;

const NoteEmailLine = styled(Text)`
  && {
    font-size: 11px;
    color: ${SA_COLORS.GOLD_700};
    font-family: monospace;
    line-height: 1.5;
  }
`;

const NoteItem = ({ item }) => {
  const isEmailNote = !!(item.emailToList || item.emailSubject);
  const hasMedia = item.mediaUrl && item.messageType !== 'TEXT';

  return (
    <NoteCard>
      <NoteHeader>
        <Icon name={isEmailNote ? 'send' : 'lock'} size={14} color={SA_COLORS.GOLD_700} />
        <NoteTitle>{item.author || 'Internal note'}</NoteTitle>
      </NoteHeader>
      <NoteTag>Internal — not visible to customer</NoteTag>
      {isEmailNote && (
        <NoteEmailMeta>
          {item.senderEmail && (
            <NoteEmailLine>
              <EmailLabel>From</EmailLabel>: {item.senderEmail}
            </NoteEmailLine>
          )}
          {item.emailToList && (
            <NoteEmailLine>
              <EmailLabel>To</EmailLabel>: {item.emailToList.join(', ')}
            </NoteEmailLine>
          )}
          {item.emailCc && item.emailCc.length > 0 && (
            <NoteEmailLine>
              <EmailLabel>CC</EmailLabel>: {item.emailCc.join(', ')}
            </NoteEmailLine>
          )}
          {item.emailBcc && item.emailBcc.length > 0 && (
            <NoteEmailLine>
              <EmailLabel>BCC</EmailLabel>: {item.emailBcc.join(', ')}
            </NoteEmailLine>
          )}
          {item.emailSubject && (
            <NoteEmailLine>
              <EmailLabel>Subject</EmailLabel>: {item.emailSubject}
            </NoteEmailLine>
          )}
        </NoteEmailMeta>
      )}
      {hasMedia &&
        (item.messageType === 'IMAGE' ? (
          <AttachImage
            src={item.mediaUrl}
            alt={item.body || 'Image'}
            onClick={() => window.open(item.mediaUrl, '_blank')}
          />
        ) : (
          <FileCardUser href={item.mediaUrl} target="_blank" rel="noopener noreferrer">
            <FileIconWrapUser>
              <Icon name="link" size={16} />
            </FileIconWrapUser>
            <FileName>{item.fileName || getFileNameFromUrl(item.mediaUrl)}</FileName>
          </FileCardUser>
        ))}
      {item.body && (
        <NoteBody>
          <MarkdownText>{item.body}</MarkdownText>
        </NoteBody>
      )}
    </NoteCard>
  );
};

// ── External message (standalone email → third party) ──────────────────

const ExternalMsgWrap = styled(FlexView)`
  width: 100%;
  max-width: 100%;
`;

const ExternalFromTrigger = styled(FilterDropdownTrigger)`
  border: none;
  border-bottom: 1px solid ${SA_COLORS.BORDER_TERTIARY};
  padding: 4px 2px;
  background: transparent;
  width: 100%;
`;

const CancelBtn = styled(Button)`
  border-radius: 0;
  padding: 6px 16px;
  border-color: ${SA_COLORS.BORDER_PRIMARY};
  background: ${SA_COLORS.BG_PRIMARY};

  &:hover:not([disabled]) {
    background: ${SA_COLORS.BG_SECONDARY};
  }

  &:hover:not([disabled]) * {
    color: ${SA_COLORS.TEXT_PRIMARY};
  }
`;

const SendBtn = styled(Button)`
  border-radius: 0;
  padding: 6px 16px;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:hover:not([disabled]) {
    opacity: 0.9;
  }
`;

const ReplyFormFoot = styled(Row)`
  gap: 8px;
`;

const ReplyFormActions = styled(Row)`
  gap: 8px;
`;

const ExternalReplyForm = ({ item, ticketId, onSent, onCancel }) => {
  const [fromEmail, setFromEmail] = useState(FROM_EMAIL_OPTIONS[0] || '');
  const [fromOpen, setFromOpen] = useState(false);
  const [toEmails, setToEmails] = useState(() => {
    if (item.direction === 'sent') return item.emailToList || [];
    return item.senderEmail ? [item.senderEmail] : [];
  });
  const [toDraft, setToDraft] = useState('');
  const [ccEmails, setCcEmails] = useState(() => item.emailCc || []);
  const [ccDraft, setCcDraft] = useState('');
  const [bccEmails, setBccEmails] = useState([]);
  const [bccDraft, setBccDraft] = useState('');
  const [showCc, setShowCc] = useState(() => (item.emailCc || []).length > 0);
  const [showBcc, setShowBcc] = useState(false);
  const [subject, setSubject] = useState(() => {
    const subj = item.emailSubject || '';
    if (!subj) return '';
    return subj.startsWith('Re:') ? subj : `Re: ${subj}`;
  });
  const [body, setBody] = useState('');
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const pendingReplyRef = useRef(null);

  const showError = useCallback(msg => {
    setError(msg);
    setTimeout(() => setError(null), ERROR_AUTO_CLEAR_MS);
  }, []);

  const {
    fileInputRef,
    uploading,
    attachment,
    setAttachment,
    handleFileSelect,
  } = useAttachmentUpload({ onError: setError });

  const { post: postMessage } = usePostMessage({
    onComplete: err => {
      setSending(false);
      if (err != null) {
        setError(typeof err === 'string' ? err : 'Failed to send reply');
        setTimeout(() => setError(null), ERROR_AUTO_CLEAR_MS);
        pendingReplyRef.current = null;
        return;
      }
      if (pendingReplyRef.current) {
        onSent(pendingReplyRef.current);
        pendingReplyRef.current = null;
      }
    },
  });

  const handleSend = useCallback(() => {
    if (!ticketId) return;
    const to = commitDraftToList(toDraft, toEmails);
    const ccResult = commitDraftToList(ccDraft, ccEmails);
    const bccResult = commitDraftToList(bccDraft, bccEmails);
    const invalid = to.invalid || ccResult.invalid || bccResult.invalid;
    if (invalid) {
      showError(`Invalid recipient email: ${invalid}`);
      return;
    }
    const toList = to.list;
    if (toList.length === 0) {
      showError('Enter a valid recipient email');
      return;
    }
    if (!body.trim() && !attachment) {
      showError('Message body is required');
      return;
    }

    const ccList = ccResult.list;
    const bccList = bccResult.list;
    const tempId = crypto.randomUUID();
    const now = new Date();
    const ts = now.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

    pendingReplyRef.current = {
      id: tempId,
      rawTs: now.toISOString(),
      kind: 'external',
      who: 'agent',
      role: 'AGENT',
      direction: 'sent',
      externalKind: item.externalKind || 'EMAIL',
      messageId: tempId,
      optimistic: true,
      body: body.trim(),
      ts,
      htmlContent: null,
      emailSubject: subject.trim() || null,
      emailCc: ccList.length ? ccList : null,
      emailBcc: bccList.length ? bccList : null,
      emailToList: toList,
      senderEmail: fromEmail || null,
      messageType: attachment ? attachment.messageType : 'TEXT',
      mediaUrl: attachment ? attachment.displayUrl || attachment.url : null,
    };

    setSending(true);
    postMessage(
      {
        type: 'EMAIL',
        content: body.trim(),
        message_type: attachment ? attachment.messageType : 'TEXT',
        ...(attachment ? { media_url: attachment.url } : {}),
        temp_id: tempId,
        ...(item.optimistic ? {} : { in_reply_to_message_id: item.messageId || item.id }),
        to_email: toList,
        from_email: fromEmail || undefined,
        email_cc: ccList.length ? ccList : undefined,
        email_bcc: bccList.length ? bccList : undefined,
        email_subject: subject.trim() || undefined,
      },
      ticketId,
    );
  }, [
    ticketId,
    toEmails,
    toDraft,
    ccEmails,
    ccDraft,
    bccEmails,
    bccDraft,
    subject,
    body,
    attachment,
    fromEmail,
    item,
    postMessage,
    showError,
  ]);

  return (
    <ReplyFormWrap gap={12} gutter={12}>
      <EmailFieldRow>
        <EmailFieldLabel>From</EmailFieldLabel>
        <FilterDropdownWrap>
          <ExternalFromTrigger type="button" onClick={() => setFromOpen(p => !p)}>
            <Text size={13} color={SA_COLORS.TEXT_PRIMARY}>
              {fromEmail || 'Select sender email'}
            </Text>
            <Icon name="chev" size={14} />
          </ExternalFromTrigger>
          {fromOpen && (
            <FilterDropdownMenu>
              {FROM_EMAIL_OPTIONS.map(email => (
                <FilterDropdownItem
                  key={email}
                  onClick={() => {
                    setFromEmail(email);
                    setFromOpen(false);
                  }}
                >
                  {email}
                </FilterDropdownItem>
              ))}
            </FilterDropdownMenu>
          )}
        </FilterDropdownWrap>
      </EmailFieldRow>
      <EmailFieldRow>
        <EmailFieldLabel>To</EmailFieldLabel>
        <RecipientChipsInput
          value={toEmails}
          onChange={setToEmails}
          draft={toDraft}
          onDraftChange={setToDraft}
          placeholder="Type an email, then press Enter or comma"
          onError={showError}
        />
        <CcBccToggleRow>
          {!showCc && <BccToggle onClick={() => setShowCc(true)}>CC</BccToggle>}
          {!showBcc && <BccToggle onClick={() => setShowBcc(true)}>BCC</BccToggle>}
        </CcBccToggleRow>
      </EmailFieldRow>
      {showCc && (
        <EmailFieldRow>
          <EmailFieldLabel>CC</EmailFieldLabel>
          <RecipientChipsInput
            value={ccEmails}
            onChange={setCcEmails}
            draft={ccDraft}
            onDraftChange={setCcDraft}
            placeholder="Type an email, then press Enter or comma"
            onError={showError}
          />
        </EmailFieldRow>
      )}
      {showBcc && (
        <EmailFieldRow>
          <EmailFieldLabel>BCC</EmailFieldLabel>
          <RecipientChipsInput
            value={bccEmails}
            onChange={setBccEmails}
            draft={bccDraft}
            onDraftChange={setBccDraft}
            placeholder="Type an email, then press Enter or comma"
            onError={showError}
          />
        </EmailFieldRow>
      )}
      <EmailFieldRow>
        <EmailFieldLabel>Subject</EmailFieldLabel>
        <EmailFieldInput
          name="subject"
          value={subject}
          onChange={e => setSubject(e.target.value)}
          placeholder="Email subject"
        />
      </EmailFieldRow>
      <EmailComposerArea
        name="message"
        value={body}
        onChange={e => setBody(e.target.value)}
        placeholder="Write your reply..."
        rows={4}
      />
      {attachment && (
        <AttachmentChip gap={6} gutter={10} alignItems="center">
          <AttachmentChipName variant="body3" color={SA_COLORS.TEXT_PRIMARY}>
            {attachment.name}
          </AttachmentChipName>
          <AttachmentChipRemove onClick={() => setAttachment(null)}>
            <Icon name="close" size={12} />
          </AttachmentChipRemove>
        </AttachmentChip>
      )}
      {error && <ModalErrorMsg>{error}</ModalErrorMsg>}
      <ReplyFormFoot justifyContent="space-between" alignItems="center">
        <HiddenFileInput
          ref={fileInputRef}
          type="file"
          accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.txt,.doc,.docx"
          onChange={handleFileSelect}
        />
        <AttachBtn
          type="button"
          variant="tertiary"
          width="auto"
          size="small"
          $padding="6px 10px"
          iconConfig={{ position: 'left', imgLink: AttachIcon }}
          color={SA_COLORS.TEXT_SECONDARY}
          onClick={() => fileInputRef.current?.click()}
          isDisabled={uploading}
        />
        <ReplyFormActions>
          <CancelBtn
            type="button"
            variant="secondary"
            width="auto"
            size="small"
            color={SA_COLORS.TEXT_PRIMARY}
            label="Cancel"
            onClick={onCancel}
          />
          <SendBtn
            type="button"
            variant="tertiary"
            width="auto"
            size="small"
            color={COLORS.WHITE}
            backgroundColor={SA_COLORS.NEUTRAL_1000}
            label={sending ? 'Sending...' : 'Send'}
            onClick={handleSend}
            isDisabled={sending || uploading}
          />
        </ReplyFormActions>
      </ReplyFormFoot>
    </ReplyFormWrap>
  );
};

const ExternalMsgItem = ({ item, ticketId, isLatestExternal, onReplySent }) => {
  const [replying, setReplying] = useState(false);
  const rightSide = item.direction === 'sent';
  const senderDisplayName = rightSide
    ? item.senderName || 'Agent'
    : item.senderEmail || 'External';
  const senderInitials =
    (senderDisplayName.match(/[a-zA-Z0-9]/g) || [])
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?';
  const canReply = Boolean(ticketId) && isLatestExternal;

  const handleSent = useCallback(
    newItem => {
      setReplying(false);
      if (onReplySent) onReplySent(newItem);
    },
    [onReplySent],
  );

  return (
    <ExternalMsgWrap>
      <ExternalMsgCard>
        <ExternalMsgStrip
          gap={8}
          gutter={14}
          alignItems="center"
          justifyContent="space-between"
        >
          <ExternalMediumLabel alignItems="center">
            <Icon name="mail" size={13} color={SA_COLORS.GOLD_700} />
            <Text variant="eyebrow2" size={11} weight={600} color={SA_COLORS.GOLD_700}>
              EXTERNAL {item.externalKind || 'EMAIL'}
            </Text>
            <DirectionLabel size={9.5} weight={700} color={SA_COLORS.TEXT_PRIMARY}>
              {item.direction === 'sent' ? '↗ Sent' : '↙ Received'}
            </DirectionLabel>
          </ExternalMediumLabel>
          <ExternalStripRight alignItems="center">
            <ExternalLockChip size={10} weight={500} color={SA_COLORS.TEXT_SECONDARY}>
              <Icon name="lock" size={10} color={SA_COLORS.TEXT_SECONDARY} />
              Internal · not visible to customer
            </ExternalLockChip>
            {item.ts && (
              <Text size={11} color={SA_COLORS.TEXT_SECONDARY}>
                {item.ts}
              </Text>
            )}
          </ExternalStripRight>
        </ExternalMsgStrip>
        <ExternalSenderRow gap={10} gutter={14} alignItems="center">
          <ExternalAvatar $variant={rightSide ? 'agent' : 'external'}>
            {senderInitials}
          </ExternalAvatar>
          <MsgName>{senderDisplayName}</MsgName>
          <MsgRole>{rightSide ? 'Agent' : 'External'}</MsgRole>
        </ExternalSenderRow>
        <ExternalHeaderGrid gap={10} gutter={12}>
          {item.senderEmail && (
            <>
              <Text variant="body3" color={SA_COLORS.TEXT_TERTIARY}>
                From
              </Text>
              <ExternalHeaderValue variant="body3" color={SA_COLORS.TEXT_PRIMARY}>
                {item.senderEmail}
              </ExternalHeaderValue>
            </>
          )}
          {item.emailToList && item.emailToList.length > 0 && (
            <>
              <Text variant="body3" color={SA_COLORS.TEXT_TERTIARY}>
                To
              </Text>
              <ExternalHeaderValue variant="body3" color={SA_COLORS.TEXT_PRIMARY}>
                {item.emailToList.join(', ')}
              </ExternalHeaderValue>
            </>
          )}
          {item.emailCc && item.emailCc.length > 0 && (
            <>
              <Text variant="body3" color={SA_COLORS.TEXT_TERTIARY}>
                CC
              </Text>
              <ExternalHeaderValue variant="body3" color={SA_COLORS.TEXT_PRIMARY}>
                {item.emailCc.join(', ')}
              </ExternalHeaderValue>
            </>
          )}
          {item.emailBcc && item.emailBcc.length > 0 && (
            <>
              <Text variant="body3" color={SA_COLORS.TEXT_TERTIARY}>
                BCC
              </Text>
              <ExternalHeaderValue variant="body3" color={SA_COLORS.TEXT_PRIMARY}>
                {item.emailBcc.join(', ')}
              </ExternalHeaderValue>
            </>
          )}
          {item.emailSubject && (
            <>
              <Text variant="body3" color={SA_COLORS.TEXT_TERTIARY}>
                Subject
              </Text>
              <ExternalHeaderValue
                variant="body3"
                color={SA_COLORS.TEXT_PRIMARY}
                weight={700}
              >
                {item.emailSubject}
              </ExternalHeaderValue>
            </>
          )}
        </ExternalHeaderGrid>
        <ExternalBody gap={14} gutter={14}>
          <AttachmentContent item={item} isUser={false} mediaLast />
        </ExternalBody>
        {canReply && <Separator height={1} />}
        {canReply && !replying && (
          <ReplyToggleBtn
            type="button"
            variant="tertiary"
            width="auto"
            size="small"
            color={SA_COLORS.GOLD_700}
            label="Reply"
            onClick={() => setReplying(true)}
          />
        )}
        {canReply && replying && (
          <ExternalReplyForm
            item={item}
            ticketId={ticketId}
            onSent={handleSent}
            onCancel={() => setReplying(false)}
          />
        )}
      </ExternalMsgCard>
    </ExternalMsgWrap>
  );
};

// ── ThreadItem (main export) ───────────────────────────────────────────

const ThreadItem = ({
  item,
  emailFrom,
  emailTo,
  onOptionSelect,
  userOnRight,
  ticketId,
  isLatestExternal,
  onExternalReplySent,
}) => {
  if (!item) return null;

  switch (item.kind) {
    case 'event':
      return <EventItem item={item} />;
    case 'msg':
      return (
        <MsgItem
          item={item}
          emailFrom={emailFrom}
          emailTo={emailTo}
          onOptionSelect={onOptionSelect}
          userOnRight={userOnRight}
        />
      );
    case 'external':
      return (
        <ExternalMsgItem
          item={item}
          ticketId={ticketId}
          isLatestExternal={isLatestExternal}
          onReplySent={onExternalReplySent}
        />
      );
    case 'system':
      return <SystemItem item={item} />;
    case 'note':
      return <NoteItem item={item} />;
    default:
      return null;
  }
};

export default ThreadItem;
